import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useImpersonationStore } from "../impersonation-store";

/**
 * Settings-facing impersonation actions.
 * Store writes reset every query: tenants, permissions and user-owned
 * settings all depend on who is being impersonated. Resetting (not just
 * invalidating) drops the previous user's data, so nothing picks an
 * organisation from the old tenant list while the new one loads — the new
 * user may not belong to it, and the tenant route would send them to Not Found.
 */
export function useImpersonationSettings() {
  const queryClient = useQueryClient();
  const impersonatedUserId = useImpersonationStore((s) => s.impersonatedUserId);
  const impersonatedLabel = useImpersonationStore((s) => s.impersonatedLabel);
  const impersonatedEmail = useImpersonationStore((s) => s.impersonatedEmail);
  const setImpersonationState = useImpersonationStore((s) => s.setImpersonation);
  const clearImpersonationState = useImpersonationStore((s) => s.clearImpersonation);

  const resetAllQueries = useCallback(() => {
    void queryClient.resetQueries();
  }, [queryClient]);

  const setImpersonation = useCallback(
    (userId: string, label: string, email: string) => {
      setImpersonationState(userId, label, email);
      resetAllQueries();
    },
    [setImpersonationState, resetAllQueries],
  );

  const clearImpersonation = useCallback(() => {
    clearImpersonationState();
    resetAllQueries();
  }, [clearImpersonationState, resetAllQueries]);

  return {
    impersonatedUserId,
    impersonatedLabel,
    impersonatedEmail,
    setImpersonation,
    clearImpersonation,
  };
}
